import { Injectable } from "@angular/core";
import moment from "moment";
import { BehaviorSubject, Observable, from, fromEvent, interval } from "rxjs";
import { filter, map, mergeMap, startWith, switchMap, tap, throttleTime } from "rxjs/operators";
import { SessionTimeoutService } from "../session-timeout/session-timeout.service";

@Injectable({
    providedIn: 'root',
})
export class LastActiveService {
    private localStorageKey: string = '__lastActive';
    private events: string[] = ['keydown', 'click'];


    private lastActive: BehaviorSubject<Date>;
    public lastActive$: Observable<Date>;

    public sessionExpired$: BehaviorSubject<boolean> = new BehaviorSubject(false);

    private recordTimeoutMs: number = 500;
    private sessionTimeoutMinutes = 55;

    constructor(private sessionTimeoutService: SessionTimeoutService) {
        const lastActiveDate = this.getLastActiveFromLocalStorage() ?? new Date();
        this.lastActive = new BehaviorSubject<Date>(lastActiveDate);
        this.lastActive$ = this.lastActive.asObservable();
        this.sessionExpired$.next(false);
        localStorage.removeItem('idleStateDate');
    }

    public resetSessionTimeout() {
        this.sessionExpired$.next(false);
        localStorage.removeItem('idleStateDate');
        this.recordLastActiveDate()
    }

    public setUp() {
        this.sessionExpired$.next(false);
        from(this.events).pipe(mergeMap(event => fromEvent(document, event)), throttleTime(this.recordTimeoutMs)).subscribe(_ => this.recordLastActiveDate());

        interval(1000)
            .pipe(
                startWith(0),
                tap((count) => {
                    if (!this.sessionExpired$.value) {
                        localStorage.setItem(
                            'idleStateDate',
                            new Date().toString()
                        );
                    }
                }),
                filter((a) => !this.sessionExpired$.value),
                throttleTime(this.recordTimeoutMs),
                tap(() => this.isSessionExpired())
            ).subscribe();
    }

    public getLastActiveDate(): Date {
        return this.lastActive.value;
    }

    public isSessionExpired() {
        var currentDate = moment(new Date());
        const diffInMinutes = moment.duration(currentDate.diff(this.getLastActiveDate())).asMinutes();
        if (diffInMinutes > this.sessionTimeoutMinutes) {
            this.closeActiveModals();
            this.sessionTimeoutService.showSessionAlert();
            this.sessionExpired$.next(true);
        } else {
            this.sessionTimeoutService.hideSessionAlert();
            this.sessionExpired$.next(false);
        }
    }

    private closeActiveModals() {
        (document.querySelectorAll('#header .material-icons')[0] as any)?.click();
        (document.querySelectorAll('mat-dialog-container .closeIcon')[0] as any)?.click();
        (document.querySelectorAll('.iconClose')[0] as any)?.click();
    }

    private recordLastActiveDate() {
        var currentDate = moment(new Date());
        if (moment.duration(currentDate.diff(this.getLastActiveDate())).asMilliseconds() < this.recordTimeoutMs) {
            return;
        }

        localStorage.setItem(this.localStorageKey, currentDate.toString());
        this.lastActive.next(currentDate.toDate());
    }

    private getLastActiveFromLocalStorage(): Date | null {
        const valueFromStorage = localStorage.getItem(this.localStorageKey);
        if (!valueFromStorage) {
            return null;
        }

        return new Date(valueFromStorage);
    }
}